import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ListItem from '../../components/shared/ListItem';
import getVehicleByVIN from '../../utils/getVehicleByVIN';
import LinkToArea from '../../components/shared/LinkToArea';
import LinkToVehicle from '../../components/shared/LinkToVehicle';
import { tripInfoStyles } from './TripInfo.styles';
import { formatDistance, formatDuration, getAreaById } from '../../utils';
import ViolationList from '../../components/TripDrawer/ViolationList';
import InvoiceItem from '../../components/TripDrawer/InvoiceItem';
import { selectAreas } from '../../redux/store/appData/areas';
import { selectCars } from '../../redux/store/userData/cars';
import {
  selectSelectedTrip,
  selectTripInvoiceData,
  selectUpdatingTrip,
  startTrip,
  updateTrip,
} from '../../redux/store/userData/trips';
import LinkToCircle from '../shared/LinkToCircle';
import { selectCircles } from '../../redux/store/userData/circles';
import getCircleById from '../../utils/getCircleById';
import {
  selectAdminBrands,
  selectAuth,
} from '../../redux/store/userData/user/selectors';
import {
  openConfirmModal,
  openModal,
} from '../../redux/store/ui/common/actions';
import formatDateTime from '../../utils/formatDateTime';
import ListEditItem from '../shared/ListEditItem';
import { selectTripEvents } from '../../redux/store/userData/vehicleEvents/selectors';
import { VehicleEventData } from '../../@types';
import { useIntl } from 'react-intl';
import { Switch, Tooltip } from 'antd';
import LoadingIcon from '../shared/LoadingIcon';
import LinkToUser from '../shared/LinkToUser';

const TripInfo = () => {
  const intl = useIntl();
  const cars = useSelector(selectCars);
  const areas = useSelector(selectAreas);
  const circles = useSelector(selectCircles);
  const {
    id,
    merchantRef,
    status,
    chassisNumber,
    tripStart,
    userReferralCode,
    userMerchantRef,
    userId,
    areaId,
    carId,
    circleId,
    circleCode,
    duration,
    distance,
    endViolations,
    backBy,
    tripEnd,
    userBlocked,
    tripEndAllroundPhotosMandatory,
    tripRefuelBlocked,
    userRefuelBlocked,
    refuelBlocked,
  } = useSelector(selectSelectedTrip) as any;
  const dispatch = useDispatch();
  const user = useSelector(selectAuth);
  const selectTripInvoice = useSelector(selectTripInvoiceData);
  const selectedTripEvents = useSelector(selectTripEvents);
  const adminBrands = useSelector(selectAdminBrands);
  const updatingTrip = useSelector(selectUpdatingTrip);
  const isDigitalFuelEnabledForBrand = adminBrands[0]?.digitalFuelEnabled;
  const vehicle = getVehicleByVIN(cars, chassisNumber);
  const area = getAreaById(areas, areaId);
  const circle = getCircleById(circles!, circleId);
  const isTripDone = status === 'DONE' && !!tripStart;
  const isTripInProgress = ['OPEN', 'IN_PROGRESS'].includes(status);
  const isTripReserved = ['RESERVED'].includes(status);
  const initialUnlockDate = selectedTripEvents?.find(
    (ve: VehicleEventData) => ve.type === 'tripStarted'
  )?.addedAt;

  const onFinishTripClicked = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    dispatch(openModal('TripCancelModal'));
  };

  const onBlockUserClicked = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    dispatch(
      openConfirmModal({
        destructive: true,
        title: intl.formatMessage({
          id: `fleetadmin.trip.block.user.msg`,
        }),
        onConfirm: () => {
          dispatch(updateTrip(id, { userBlocked: true }));
        },
      })
    );
  };

  const onUnblockUserClicked = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    dispatch(
      openConfirmModal({
        title: intl.formatMessage({
          id: `fleetadmin.trip.unblock.user.msg`,
        }),
        onConfirm: () => {
          dispatch(updateTrip(id, { userBlocked: false }));
        },
      })
    );
  };
  const onStartTripClicked = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    dispatch(startTrip(id));
  };

  const onEditReturnTime = () => {
    dispatch(openModal('TripReturnTimeEditModal'));
  };

  const returnTime = tripEnd ? tripEnd : backBy;
  return (
    <>
      <div className="trip-info">
        <ListItem
          testid="ContractID"
          label={'Contract ID'}
          value={merchantRef}
        />
        <ListItem
          testid="VIN"
          label={'VIN'}
          value={
            vehicle ? (
              <LinkToVehicle carId={carId} text={chassisNumber} />
            ) : (
              <></>
            )
          }
        />
        <ListItem
          testid="UserCode"
          label={'User code'}
          value={
            <LinkToUser
              userId={userId}
              userCode={userReferralCode}
              label={`${userReferralCode} (${userMerchantRef})`}
            />
          }
        />
        {(circle?.name || circleCode) && (
          <ListItem
            testid="UserCircle"
            label={'User Circle'}
            value={
              circle?.name ? (
                <LinkToCircle circleId={circleId} text={circle.name} />
              ) : (
                circleCode
              )
            }
          />
        )}
        <ListItem
          testid="ReturnArea"
          label={'Return Area'}
          value={area ? <LinkToArea area={area} text={area?.name} /> : '-'}
        />
        {initialUnlockDate && (
          <ListItem
            testid="StartedAt"
            label={intl.formatMessage({
              id: 'fleetadmin.trip.info.tripstarted.label',
            })}
            value={<span>{formatDateTime(initialUnlockDate)}</span>}
          />
        )}
        {returnTime && (
          <ListEditItem
            testid="ReturnTime"
            label={intl.formatMessage({
              id: 'fleetadmin.trip.info.backby.label',
            })}
            leftItem={<span>{formatDateTime(returnTime)}</span>}
            rightItem={
              user?.permissions?.fleetManager &&
              isTripInProgress && (
                <img
                  className="edit-button"
                  src={'/images/drawer/edit.svg'}
                  alt="edit"
                  onClick={onEditReturnTime}
                />
              )
            }
          />
        )}
        <ListItem
          testid="Insurance"
          label={'Insurance'}
          value={
            !['CANCELLED', 'EXPIRED'].includes(status)
              ? selectTripInvoice?.insurance
              : '-'
          }
        />
        <ListItem
          testid="Duration"
          label={'Duration'}
          value={isTripDone ? formatDuration(duration) : '-'}
        />
        <ListItem
          testid="Mileage"
          label={'Mileage'}
          value={isTripDone ? formatDistance(distance) : '-'}
        />
        <ListItem
          testid="EndViolations"
          label={'End Violations'}
          value={
            isTripDone ? <ViolationList violations={endViolations} /> : '-'
          }
        />
        <ListItem
          testid="AllAroundPhotosMandatory"
          label={intl.formatMessage({
            id: 'fleetadmin.trip.tripEndAllroundPhotosMandatory',
            defaultMessage: 'All-around photos mandatory',
          })}
          value={
            tripEndAllroundPhotosMandatory
              ? intl.formatMessage({
                  id: 'fleetadmin.common.yes',
                  defaultMessage: 'Yes',
                })
              : intl.formatMessage({
                  id: 'fleetadmin.common.no',
                  defaultMessage: 'No',
                })
          }
        />
        <ListItem
          testid="Invoice"
          label={'Invoice'}
          value={
            isTripDone && selectTripInvoice?.invoiceName ? (
              <InvoiceItem
                invoiceName={selectTripInvoice?.invoiceName}
                invoiceUri={selectTripInvoice?.invoiceUri}
              />
            ) : (
              '-'
            )
          }
        />
        {isDigitalFuelEnabledForBrand &&
          user?.permissions?.tripsManager &&
          (isTripInProgress || isTripReserved) && (
            <ListEditItem
              testid="TripRefuelBlocked"
              leftItem={
                <div className="permission-label">
                  {intl.formatMessage({
                    id: `fleetadmin.status.digital.fuel`,
                    defaultMessage: 'Digital Fueling Enabled',
                  })}
                </div>
              }
              rightItem={
                <>
                  {updatingTrip ? <LoadingIcon /> : null}
                  <Tooltip
                    title={
                      userRefuelBlocked
                        ? intl.formatMessage({
                            id: 'fleetadmin.status.digital.fuel.user.blocked',
                            defaultMessage: 'Blocked for this user',
                          })
                        : undefined
                    }
                  >
                    <span>
                      <Switch
                        checked={!refuelBlocked}
                        disabled={userRefuelBlocked}
                        onChange={() => {
                          dispatch(
                            updateTrip(id, {
                              tripRefuelBlocked: !tripRefuelBlocked,
                            })
                          );
                        }}
                      />
                    </span>
                  </Tooltip>
                </>
              }
            />
          )}
        {user?.permissions?.tripsManager &&
          isTripInProgress &&
          (userBlocked ? (
            <div
              className="trip-info-unblock-button"
              onClick={onUnblockUserClicked}
            >
              {intl.formatMessage({
                id: 'fleetadmin.trip.unblock.user',
              })}
            </div>
          ) : (
            <div
              className="trip-info-block-button"
              onClick={onBlockUserClicked}
            >
              {intl.formatMessage({
                id: 'fleetadmin.trip.block.user',
              })}
            </div>
          ))}
        {user?.permissions?.tripsManager && isTripInProgress && (
          <div
            className="trip-info-finish-button"
            onClick={onFinishTripClicked}
          >
            Finish trip
          </div>
        )}
        {user?.permissions?.tripsManager && isTripReserved && (
          <div className="trip-info-finish-button" onClick={onStartTripClicked}>
            Start trip
          </div>
        )}
      </div>
      <style jsx>{tripInfoStyles}</style>
    </>
  );
};

export default TripInfo;
